import { FC, useCallback, useContext, useMemo, useState } from "react";
import Timeline from "@mui/lab/Timeline";
import {
  TabContext,
  TabList,
  TabPanel,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import {
  Box,
  capitalize,
  Colors,
  Drawer,
  List,
  ListItem,
  Tab,
} from "@analyticsMD/quil";
import History from "@mui/icons-material/History";
import UpdateHistoryEntry from "../types/UpdateHistoryEntry";
import { Sources } from "../types/Sources";
import { css } from "@emotion/css";
import moment from "moment";
import APIContext from "../contexts/APIContext";
import { TypeChoices } from "../types/TypeChoices";
import EDD from "../types/EDD";
import PatientDisposition from "../types/PatientDisposition";
import { DATE_TIME_FORMAT } from "../utils/utils";
import { UI_ACCENT_BLUE } from "../constants/ui-colors";
import DrawerHeader from "./drawer-header";

const sxStyles = {
  icon: {
    marginRight: "0.25rem",
    color: `${UI_ACCENT_BLUE} !important`,
    cursor: "pointer",
  },
  close: { alignSelf: "flex-end", cursor: "pointer" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    margin: "1.5rem",
  },
  drawerPanel: {
    width: "min(400px, 100vw)",
    maxWidth: "400px",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    boxSizing: "border-box",
  },
  /** Long disposition lists: label on its own row, value wraps below */
  listItemValueStack: {
    display: "block",
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
  },
  /** Short metadata: label and value on one row (wraps as a unit if needed) */
  listItemMetaInline: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "baseline",
    columnGap: "0.25rem",
    rowGap: "0.125rem",
    paddingTop: "0.125rem",
    paddingBottom: "0.125rem",
  },
  historyFieldLabel: {
    color: Colors.grey_30,
    fontSize: "0.8rem",
    display: "block",
    marginBottom: "0.125rem",
    whiteSpace: "nowrap",
  },
  historyInlineLabel: {
    color: Colors.grey_30,
    fontSize: "0.85rem",
    flexShrink: 0,
  },
  historyFieldValue: {
    fontSize: "0.9rem",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  historyInlineValue: {
    fontSize: "0.9rem",
    minWidth: 0,
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  clickableContainer: {
    display: "flex",
    alignItems: "center",
    color: UI_ACCENT_BLUE,
    fontWeight: "500",
    fontSize: "0.775rem",
    cursor: "pointer",
  },
};

const cssStyles = {
  defaultText: css`
    color: ${Colors.grey_40};
    font-size: 0.85rem;
    margin-left: 0.25rem;
  `,
};

const humanizedSources = {
  [Sources.AUTO_POPULATED]: "Qventus Autopopulated",
  [Sources.EHR_GENERATED]: "EHR",
  [Sources.QVENTUS_SUGGESTED]: "Qventus Accepted Suggestion",
  [Sources.USER_CREATED]: "QCard",
};

const DISP_SNAPSHOT_TIME_KEY = "YYYY-MM-DDTHH:mm:ss";

const dispositionDisplayName = (d: PatientDisposition): string => {
  if (typeof d.disposition === "number") return String(d.disposition);
  return d.disposition?.display_name ?? "";
};

const dispositionEntryMeta = (d: PatientDisposition) => ({
  updated_by: d.updated_by_name
    ? capitalize(d.updated_by_name.toLowerCase())
    : d.updated_by
    ? d.updated_by
    : "Qventus",
  source: d.source as Sources,
});

/**
 * With multiple user-entered rows, each history line is one slot; batched saves share a timestamp.
 * Build one timeline row per distinct update second showing the full set of disposition names
 * active after that moment (so consecutive rows show adds/removes vs the full list).
 */
const buildMultiDispositionSnapshotHistory = (
  userDispositions: PatientDisposition[]
): UpdateHistoryEntry[] => {
  if (!userDispositions.length) {
    return [];
  }

  type VersionRow = {
    id: number;
    value: string;
    ts: string;
    /** Precomputed to avoid repeated moment() in hot paths */
    tsMs: number;
    secondKey: string;
    updated_by: string;
    source: Sources;
  };

  const lookbackStart = moment().subtract(1, "weeks");
  const lookbackStartMs = lookbackStart.valueOf();
  const versions: VersionRow[] = [];

  for (const pd of userDispositions) {
    const id = pd.id;
    if (id == null) continue;
    const meta = dispositionEntryMeta(pd);

    if (pd.history) {
      for (const h of pd.history) {
        if (!h.qv_updated_ts) continue;
        const m = moment(h.qv_updated_ts);
        const tsMs = m.valueOf();
        if (tsMs < lookbackStartMs) continue;
        versions.push({
          id,
          value: dispositionDisplayName(h),
          ts: h.qv_updated_ts,
          tsMs,
          secondKey: m.format(DISP_SNAPSHOT_TIME_KEY),
          updated_by: h.updated_by_name
            ? capitalize(h.updated_by_name.toLowerCase())
            : h.updated_by
            ? h.updated_by
            : meta.updated_by,
          source: (h.source as Sources) ?? meta.source,
        });
      }
    }
    const currentTs = pd.qv_updated_ts || pd.qv_created_ts;
    if (currentTs) {
      const m = moment(currentTs);
      versions.push({
        id,
        value: dispositionDisplayName(pd),
        ts: currentTs,
        tsMs: m.valueOf(),
        secondKey: m.format(DISP_SNAPSHOT_TIME_KEY),
        ...meta,
      });
    }
  }

  if (versions.length === 0) {
    return [];
  }

  /** Rightmost row with tsMs <= endMs (sorted ascending by tsMs). */
  const latestVersionAtOrBefore = (
    sortedAsc: VersionRow[],
    endMs: number
  ): VersionRow | null => {
    const n = sortedAsc.length;
    if (n === 0 || sortedAsc[0].tsMs > endMs) return null;
    let lo = 0;
    let hi = n - 1;
    while (lo < hi) {
      const mid = lo + Math.floor((hi - lo + 1) / 2);
      if (sortedAsc[mid].tsMs <= endMs) lo = mid;
      else hi = mid - 1;
    }
    return sortedAsc[lo];
  };

  const byId = new Map<number, VersionRow[]>();
  const bySecond = new Map<string, VersionRow[]>();
  for (const v of versions) {
    const idList = byId.get(v.id);
    if (idList) idList.push(v);
    else byId.set(v.id, [v]);
    const secList = bySecond.get(v.secondKey);
    if (secList) secList.push(v);
    else bySecond.set(v.secondKey, [v]);
  }

  for (const list of Array.from(byId.values())) {
    list.sort((a, b) => a.tsMs - b.tsMs);
  }

  const allIds = Array.from(byId.keys());
  const snapshotSeconds = Array.from(bySecond.keys()).sort((a, b) =>
    moment(b, DISP_SNAPSHOT_TIME_KEY).diff(moment(a, DISP_SNAPSHOT_TIME_KEY))
  );

  return snapshotSeconds.map((secondKey) => {
    const batchEnd = moment(secondKey, DISP_SNAPSHOT_TIME_KEY).endOf("second");
    const endMs = batchEnd.valueOf();
    const valueParts: string[] = [];
    for (const id of allIds) {
      const chain = byId.get(id);
      const best = chain ? latestVersionAtOrBefore(chain, endMs) : null;
      if (best?.value) valueParts.push(best.value);
    }
    valueParts.sort((x, y) => x.localeCompare(y));

    const inSecond = bySecond.get(secondKey) ?? [];
    const metaSource = inSecond[0] ?? versions[0];

    return {
      id: inSecond[0]?.id ?? allIds[0] ?? 0,
      value: valueParts.join(", "),
      update_type: TypeChoices.USER_ENTERED,
      timestamp: batchEnd.toISOString(true),
      updated_by: metaSource.updated_by,
      source: metaSource.source,
      requires_default: false,
    };
  });
};

const UpdateHistory: FC = () => {
  const { eddQuery, patientDispositionsQuery, configurationQuery } =
    useContext(APIContext);

  const multipleDispositionsEnabled = Boolean(
    configurationQuery?.data?.[0]?.multiple_dispositions_enabled
  );

  const defaultText = "(default)";

  const eddUpdateHistory = useMemo(() => {
    const userEDD =
      eddQuery.isSuccess &&
      eddQuery.data?.find((edd) => edd.edd_type === TypeChoices.USER_ENTERED);

    const eddUpdates =
      userEDD && userEDD.history
        ? userEDD.history.filter((edd: EDD) => {
            const isUpdateInLookback = moment(edd.qv_updated_ts).isSameOrAfter(
              moment().subtract("1", "weeks")
            );

            return isUpdateInLookback;
          })
        : [];

    // always return the current value if there are no updates in the last week
    if (eddUpdates.length === 0 && userEDD) {
      const formattedEDD = moment(userEDD.edd_value).format("MM/DD/YY");

      return [
        {
          id: userEDD.id,
          value: `${formattedEDD}, ${userEDD.is_am_discharge ? "AM" : "PM"}`,
          update_type: userEDD.edd_type,
          timestamp:
            userEDD.edd_type === TypeChoices.AUTO_POPULATED
              ? userEDD.decision_updated_ts
              : userEDD.qv_updated_ts,
          updated_by: userEDD.updated_by_name
            ? capitalize(userEDD.updated_by_name.toLowerCase())
            : userEDD.updated_by
            ? userEDD.updated_by
            : "Qventus",
          source: userEDD.source,
          requires_default: !userEDD.is_am_discharge,
        },
      ];
    }

    return eddUpdates.map((edd: EDD) => {
      const formattedEDD = moment(edd.edd_value).format("MM/DD/YY");
      return {
        id: edd.id,
        value: `${formattedEDD}, ${edd.is_am_discharge ? "AM" : "PM"}`,
        update_type: edd.edd_type,
        timestamp:
          edd.edd_type === TypeChoices.AUTO_POPULATED
            ? edd.decision_updated_ts
            : edd.qv_updated_ts,
        updated_by: edd.updated_by_name
          ? capitalize(edd.updated_by_name.toLowerCase())
          : edd.updated_by
          ? edd.updated_by
          : "Qventus",
        source: edd.source,
        requires_default: !edd.is_am_discharge,
      };
    });
  }, [eddQuery.data, eddQuery.isSuccess]);

  const dispositionUpdateHistory = useMemo(() => {
    const userDispositions =
      patientDispositionsQuery.isSuccess && patientDispositionsQuery.data
        ? patientDispositionsQuery.data.filter(
            (disposition) =>
              disposition.disposition_type === TypeChoices.USER_ENTERED
          )
        : [];

    const mapDispositionToEntry = (
      disposition: PatientDisposition
    ): UpdateHistoryEntry => ({
      id: disposition.id,
      value:
        typeof disposition.disposition === "number"
          ? String(disposition.disposition)
          : disposition.disposition?.display_name ?? "",
      update_type: disposition.disposition_type as TypeChoices,
      timestamp:
        disposition.disposition_type === TypeChoices.AUTO_POPULATED
          ? disposition.decision_updated_ts
          : disposition.qv_updated_ts,
      updated_by: disposition.updated_by_name
        ? capitalize(disposition.updated_by_name.toLowerCase())
        : disposition.updated_by
        ? disposition.updated_by
        : "Qventus",
      source: disposition.source as Sources,
      requires_default: false,
    });

    if (multipleDispositionsEnabled) {
      return buildMultiDispositionSnapshotHistory(userDispositions);
    }

    const dispositionUpdates = userDispositions.flatMap((dispo) =>
      dispo.history
        ? dispo.history.filter((h: PatientDisposition) =>
            moment(h.qv_updated_ts).isSameOrAfter(
              moment().subtract("1", "weeks")
            )
          )
        : []
    );

    // Single user-entered row: list each history revision (or current) as its own row
    if (dispositionUpdates.length === 0 && userDispositions.length > 0) {
      return userDispositions
        .map(mapDispositionToEntry)
        .sort((a, b) =>
          moment(b.timestamp || 0).diff(moment(a.timestamp || 0))
        );
    }

    return dispositionUpdates
      .map(mapDispositionToEntry)
      .sort((a, b) =>
        moment(b.timestamp || 0).diff(moment(a.timestamp || 0))
      );
  }, [
    patientDispositionsQuery.data,
    patientDispositionsQuery.isSuccess,
    multipleDispositionsEnabled,
  ]);

  const generateTimelineContent = useCallback(
    (update: UpdateHistoryEntry, historyTab: "EDD" | "Disposition") => {
      const formattedTimestamp = update.timestamp
        ? moment(update.timestamp).format(DATE_TIME_FORMAT)
        : "";

      const valueStacked =
        historyTab === "Disposition" && multipleDispositionsEnabled;

      return (
        <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
          <List dense disablePadding sx={{ maxWidth: "100%" }}>
            <ListItem
              sx={
                valueStacked
                  ? sxStyles.listItemValueStack
                  : sxStyles.listItemMetaInline
              }
              disableGutters
            >
              <Box
                component="span"
                sx={
                  valueStacked
                    ? sxStyles.historyFieldLabel
                    : sxStyles.historyInlineLabel
                }
              >
                Value:
              </Box>
              <Box
                component={valueStacked ? "div" : "span"}
                sx={
                  valueStacked
                    ? sxStyles.historyFieldValue
                    : sxStyles.historyInlineValue
                }
              >
                {update.value}
                {update.requires_default && (
                  <span className={cssStyles.defaultText}>{defaultText}</span>
                )}
              </Box>
            </ListItem>
            <ListItem sx={sxStyles.listItemMetaInline} disableGutters>
              <Box component="span" sx={sxStyles.historyInlineLabel}>
                Updated by:
              </Box>
              <Box component="span" sx={sxStyles.historyInlineValue}>
                {update.updated_by}
              </Box>
            </ListItem>
            <ListItem sx={sxStyles.listItemMetaInline} disableGutters>
              <Box component="span" sx={sxStyles.historyInlineLabel}>
                Source:
              </Box>
              <Box component="span" sx={sxStyles.historyInlineValue}>
                {humanizedSources[update.source]}
              </Box>
            </ListItem>
            <ListItem sx={sxStyles.listItemMetaInline} disableGutters>
              <Box component="span" sx={sxStyles.historyInlineLabel}>
                Update time:
              </Box>
              <Box component="span" sx={sxStyles.historyInlineValue}>
                {formattedTimestamp}
              </Box>
            </ListItem>
          </List>
        </Box>
      );
    },
    [multipleDispositionsEnabled]
  );

  const [showHistory, setShowHistory] = useState(false);

  const updates = {
    EDD: eddUpdateHistory,
    Disposition: dispositionUpdateHistory,
  };

  const updateTypes = Object.keys(updates);

  const [tabValue, setTabValue] = useState(updateTypes[0]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const resetTabs = () => {
    setShowHistory(false);
    setTabValue(updateTypes[0]);
  };

  const handleHistoryClick = () => {
    setShowHistory(!showHistory);
  };

  return (
    <>
      <Box sx={sxStyles.clickableContainer} onClick={handleHistoryClick}>
        <History sx={sxStyles.icon} />
        <span>View History</span>
      </Box>
      <Drawer
        anchor="right"
        open={showHistory}
        onClose={() => resetTabs()}
        ModalProps={{
          BackdropProps: { sx: { background: "none" } },
        }}
        PaperProps={{
          sx: sxStyles.drawerPanel,
        }}
      >
        <DrawerHeader title="Recent History" onClose={() => resetTabs()} />
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: Colors.grey_15 }}>
            <TabList
              TabIndicatorProps={{
                sx: { backgroundColor: UI_ACCENT_BLUE },
              }}
              onChange={handleTabChange}
            >
              {updateTypes.map((key) => (
                <Tab
                  label={key}
                  key={key}
                  value={key}
                  sx={{ textTransform: "capitalize" }}
                />
              ))}
            </TabList>
          </Box>
          <Timeline
            sx={{
              overflowY: "auto",
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {Object.entries(updates).map(([entryType, entries]) => {
              const generateEntryTimeline = () =>
                entries
                  .sort((a, b) =>
                    moment(b.timestamp).diff(moment(a.timestamp), "seconds")
                  )
                  .map((entry, idx) => (
                    <TimelineItem
                      key={`${entry.id}-${entry.timestamp ?? "na"}-${idx}`}
                    >
                      <TimelineSeparator>
                        <TimelineDot
                          sx={{
                            backgroundColor: UI_ACCENT_BLUE,
                            boxShadow: "none",
                          }}
                        />
                        {idx < entries.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent
                        sx={{
                          paddingTop: ".5rem",
                          flex: 1,
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      >
                        {generateTimelineContent(
                          entry,
                          entryType as "EDD" | "Disposition"
                        )}
                      </TimelineContent>
                    </TimelineItem>
                  ));

              return (
                <TabPanel
                  key={entryType}
                  value={entryType}
                  sx={{ minWidth: 0, maxWidth: "100%", px: 1.5 }}
                >
                  {generateEntryTimeline()}
                </TabPanel>
              );
            })}
          </Timeline>
        </TabContext>
      </Drawer>
    </>
  );
};

export default UpdateHistory;
